import { FvcSession } from "webportalclient";
import { SpirometryAnalysisSetTypeEnum } from "../../misc/api/ApiSpirometryAnalysisSetType";
import { HomeSpirometryStatus, StudyFvcSetting } from "../../misc/api/apiTypes";
import { ParameterTypeName } from "../../misc/api/ParameterTypes";
import { WebPortalLegacyClientInternal } from "../../misc/api/WebPortalTokenClient";
import { FvcParameters } from "../Shared/PFTAPITypes";
import { calculateSessionGrading } from "../Shared/TestQualityGrading";
import { DoneLoadingBestValuesAction, DoneLoadingReversibilityChangesAction, LoadingBestValuesAction, LoadingReversibilityChangesAction } from "../Shared/UseAcceptability";
import { AcceptabilityNextGenState, UpdateBestTestNextGenAction, UpdateGradingAction, UpdateReversibilityChangeNextGenAction } from "../Shared/UseAcceptabilityNextGen";

export const calculateBestValuesGrading = async (legacyClient: WebPortalLegacyClientInternal, acceptabilityState: AcceptabilityNextGenState, acceptabilityDispatch: React.Dispatch<any>, fvcSession: FvcSession, fvcSettings: StudyFvcSetting, calculateGrading = true) => {
    acceptabilityDispatch(LoadingBestValuesAction.create());

    const bestTests = await legacyClient.calculateFvcBestTestsForAnalysisSet(acceptabilityState.analysisSet, fvcSession);

    const getBest = (param: ParameterTypeName): number => {
        return bestTests.find(bt => bt.ParameterType === param)?.Value ?? 0;
    }

    const fvcParameters: FvcParameters = {
        Fvc: getBest(ParameterTypeName.PARAM_FVC),
        Fev1: getBest(ParameterTypeName.PARAM_FEV1),
        Fev6: getBest(ParameterTypeName.PARAM_FEV6),
        Tv: getBest(ParameterTypeName.PARAM_TV),
        Fivc: getBest(ParameterTypeName.PARAM_FIVC),
        PefMin: getBest(ParameterTypeName.PARAM_PEFMIN),
        PefSec: getBest(ParameterTypeName.PARAM_PEFSEC),
        PifMin: getBest(ParameterTypeName.PARAM_PIFMIN),
        PifSec: getBest(ParameterTypeName.PARAM_PIFSEC),
        Fev3: getBest(ParameterTypeName.PARAM_FEV3),
        Fev05: getBest(ParameterTypeName.PARAM_FEV05),
        Fev075: getBest(ParameterTypeName.PARAM_FEV075),
        Fiv1: getBest(ParameterTypeName.PARAM_FIV1),
        Mvvind: getBest(ParameterTypeName.PARAM_MVVIND),
        Ivc: getBest(ParameterTypeName.PARAM_IVC),
        FefMax: getBest(ParameterTypeName.PARAM_FVC_FEFMAX),
        Fev1Vc: getBest(ParameterTypeName.PARAM_FEV1VC),
        Fev1Ht2: getBest(ParameterTypeName.PARAM_FEV1HT2),
        Fev3Vc: getBest(ParameterTypeName.PARAM_FEV3VC),
        Tlc: getBest(ParameterTypeName.PARAM_TLC),
        Rv: getBest(ParameterTypeName.PARAM_RV),
        Frc: getBest(ParameterTypeName.PARAM_FRC),
        Fev1Q: getBest(ParameterTypeName.PARAM_FEV1Q),
        Fef2575: getBest(ParameterTypeName.PARAM_FEF2575),
        Fef0212: getBest(ParameterTypeName.PARAM_FEF0212),
        Fef7585: getBest(ParameterTypeName.PARAM_FEF7585),
        Fef25: getBest(ParameterTypeName.PARAM_FEF25),
        Fef50: getBest(ParameterTypeName.PARAM_FEF50),
        Fef75: getBest(ParameterTypeName.PARAM_FEF75),
        Fmft: getBest(ParameterTypeName.PARAM_FMFT),
        Fev1Fev6: getBest(ParameterTypeName.PARAM_FEV1FEV6),
        Fev1Fvc: getBest(ParameterTypeName.PARAM_FEV1FVC),
        Fev1R: getBest(ParameterTypeName.PARAM_FEV1R),
        Fev05Fvc: getBest(ParameterTypeName.PARAM_FEV05FVC),
        Fev075Fvc: getBest(ParameterTypeName.PARAM_FEV075FVC),
        Fev3Fvc: getBest(ParameterTypeName.PARAM_FEV3FVC),
        Fev6Fvc: getBest(ParameterTypeName.PARAM_FEV6FVC),
        Fef2575Fvc: getBest(ParameterTypeName.PARAM_FEF2575FVC),
        FivcFvc: getBest(ParameterTypeName.PARAM_FIVCFVC),
        Fev1Pef: getBest(ParameterTypeName.PARAM_FEV1PEF),
        Fif50Fef50: getBest(ParameterTypeName.PARAM_FIF50FEF50),
        Fev1Fivc: getBest(ParameterTypeName.PARAM_FEV1FIVC),
        Fiv1Fivc: getBest(ParameterTypeName.PARAM_FIV1FIVC),
        Fiv1Fvc: getBest(ParameterTypeName.PARAM_FIV1FVC),
        Fef50Fif50: getBest(ParameterTypeName.PARAM_FEF50FIF50),
        Fev1Ivc: getBest(ParameterTypeName.PARAM_FEV1IVC),
        Fev3Fev6: getBest(ParameterTypeName.PARAM_FEV3FEV6),
        Ic: getBest(ParameterTypeName.PARAM_IC),
        Irv: getBest(ParameterTypeName.PARAM_IRV),
        Erv: getBest(ParameterTypeName.PARAM_ERV),
        Fet: getBest(ParameterTypeName.PARAM_FET),
        Fif25: getBest(ParameterTypeName.PARAM_FIF25),
        Fif50: getBest(ParameterTypeName.PARAM_FIF50),
        Fif75: getBest(ParameterTypeName.PARAM_FIF75),
        Rawind: getBest(ParameterTypeName.PARAM_RAWIND),
        Vc: getBest(ParameterTypeName.PARAM_VC),
        Evc: getBest(ParameterTypeName.PARAM_EVC),
    };

    acceptabilityDispatch(UpdateBestTestNextGenAction.create(fvcParameters));

    if (fvcSettings.GradingEnabled && fvcSession.tests.length > 0 && calculateGrading) {
        const fev1Grade = calculateSessionGrading(acceptabilityState.analysisSet.tests, fvcSession.age, rs => rs.fev1Absolute, rs => rs.fev1Percentage, fvcSettings.IndividualAcceptabilitiesEnabled, ast => ast.fev1Acceptability);
        const fvcGrade = calculateSessionGrading(acceptabilityState.analysisSet.tests, fvcSession.age, rs => rs.fvcAbsolute, rs => rs.fvcPercentage, fvcSettings.IndividualAcceptabilitiesEnabled, ast => ast.fvcAcceptability);

        acceptabilityDispatch(UpdateGradingAction.create(fev1Grade, fvcGrade));
    }

    acceptabilityDispatch(DoneLoadingBestValuesAction.create());
}

export const calculateReversibilityChange = async (legacyClient: WebPortalLegacyClientInternal, acceptabilityState: AcceptabilityNextGenState, acceptabilityDispatch: React.Dispatch<any>, fvcSession: FvcSession) => {
    acceptabilityDispatch(LoadingReversibilityChangesAction.create());

    if ((acceptabilityState.analysisSet.type === SpirometryAnalysisSetTypeEnum.Site && fvcSession.status === HomeSpirometryStatus.Completed) || acceptabilityState.analysisSet.type === SpirometryAnalysisSetTypeEnum.Overread) {
        acceptabilityDispatch(DoneLoadingReversibilityChangesAction.create());
        return;
    }

    const reversibilityChangeParameters = await legacyClient.calculateReversibilityChange(acceptabilityState.analysisSet, fvcSession);
    const fev1 = reversibilityChangeParameters.find(bt => bt.ParameterType === ParameterTypeName.PARAM_FEV1);
    acceptabilityDispatch(UpdateReversibilityChangeNextGenAction.create(
        {
            absolute: fev1.Value,
            percentage: fev1.PercentageOfChange
        }
    ));
    acceptabilityDispatch(DoneLoadingReversibilityChangesAction.create());
}

